import homeCashBackOfferImage from 'assets/images/rewards/HomeCashBackOfferImage.png';
import { Image } from 'expo-image';
import rewards from 'features/rewards/localization/rewardsString.en';
import React, { useCallback, useState } from 'react';
import {
  ImageSourcePropType,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import theme from 'shared/styles/theme';

export enum HomeOfferCardState {
  DEFAULT = 'default',
  PRESSED = 'pressed',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  HOVER = 'hover',
  FOCUSED = 'focused',
}

export interface Copy {
  text: string;
}

export interface OfferItem {
  id: string;
  iconUrl: string;
  title: Copy;
}

export interface HomeOfferCardProps {
  id: string;
  title: Copy;
  bodyItems: readonly OfferItem[];
  buttonTitle?: Copy;
  tag?: TagProps;
  onPress: () => void;
  onActivate: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
  accessibilityHint?: string;
}

const HomeOfferCard = ({
  id: _id,
  title,
  bodyItems,
  buttonTitle,
  tag,
  onPress,
  onActivate,
  isActive = false,
  isDisabled = false,
  style,
  accessibilityLabel,
  testID,
  accessibilityHint,
}: HomeOfferCardProps) => {
  const [cardState, setCardState] = useState<HomeOfferCardState>(() => {
    if (isDisabled) {
      return HomeOfferCardState.DISABLED;
    }
    if (isActive) {
      return HomeOfferCardState.ACTIVE;
    }
    return HomeOfferCardState.DEFAULT;
  });

  const handlePressIn = useCallback(() => {
    if (cardState !== HomeOfferCardState.DISABLED) {
      setCardState(HomeOfferCardState.PRESSED);
    }
  }, [cardState]);

  const handlePressOut = useCallback(() => {
    if (cardState !== HomeOfferCardState.DISABLED) {
      setCardState(
        isActive ? HomeOfferCardState.ACTIVE : HomeOfferCardState.DEFAULT,
      );
    }
  }, [cardState, isActive]);

  const handleHoverIn = useCallback(() => {
    if (
      cardState !== HomeOfferCardState.DISABLED &&
      cardState !== HomeOfferCardState.PRESSED
    ) {
      setCardState(HomeOfferCardState.HOVER);
    }
  }, [cardState]);

  const handleHoverOut = useCallback(() => {
    if (
      cardState !== HomeOfferCardState.DISABLED &&
      cardState !== HomeOfferCardState.PRESSED
    ) {
      setCardState(
        isActive ? HomeOfferCardState.ACTIVE : HomeOfferCardState.DEFAULT,
      );
    }
  }, [cardState, isActive]);

  const handleFocus = useCallback(() => {
    if (cardState !== HomeOfferCardState.DISABLED) {
      setCardState(HomeOfferCardState.FOCUSED);
    }
  }, [cardState]);

  const handleBlur = useCallback(() => {
    if (cardState !== HomeOfferCardState.DISABLED) {
      setCardState(
        isActive ? HomeOfferCardState.ACTIVE : HomeOfferCardState.DEFAULT,
      );
    }
  }, [cardState, isActive]);

  const getContainerStyle = useCallback((): StyleProp<ViewStyle> => {
    const containerStyles: StyleProp<ViewStyle>[] = [styles.container];

    if (style) {
      containerStyles.push(style);
    }

    switch (cardState) {
      case HomeOfferCardState.PRESSED:
        containerStyles.push(styles.containerPressed);
        break;
      case HomeOfferCardState.ACTIVE:
        containerStyles.push(styles.containerActive);
        break;
      case HomeOfferCardState.DISABLED:
        containerStyles.push(styles.containerDisabled);
        break;
      case HomeOfferCardState.HOVER:
        containerStyles.push(styles.containerHover);
        break;
      case HomeOfferCardState.FOCUSED:
        containerStyles.push(styles.containerFocused);
        break;
    }

    return containerStyles;
  }, [cardState, style]);

  const renderOfferItems = useCallback(() => {
    if (!bodyItems || bodyItems.length === 0) {
      return null;
    }

    return (
      <View style={styles.offerItemsContainer}>
        <Image
          source={homeCashBackOfferImage as ImageSourcePropType}
          style={styles.backgroundImage}
          contentFit="contain"
        />
        {bodyItems.map((item, index) => (
          <View
            key={item.id}
            style={[
              styles.offerItemWrapper,
              index > 0 && styles.offerItemOverlap,
            ]}
            accessibilityLabel={item.title.text}
          >
            <IconAvatar
              variant="MD"
              backgroundColor={theme.color.background.tertiary}
              defaultPlaceholderIcon="STORE"
              imageSource={{ uri: item.iconUrl }}
              iconAccessibilityLabel={item.title.text}
            />
          </View>
        ))}
      </View>
    );
  }, [bodyItems]);

  return (
    <View>
      <Pressable
        style={getContainerStyle()}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isDisabled}
        accessible={true}
        accessibilityLabel={accessibilityLabel || `${title?.text} offer card`}
        accessibilityRole="button"
        accessibilityHint={
          accessibilityHint || 'Double tap to view offer details'
        }
        accessibilityState={{
          disabled: cardState === HomeOfferCardState.DISABLED,
          selected: cardState === HomeOfferCardState.ACTIVE,
        }}
        testID={testID}
      >
        <View style={styles.contentContainer}>
          <View style={styles.iconSection}>{renderOfferItems()}</View>

          {tag ? (
            <View style={styles.tagContainer}>
              <Tag text={tag?.text} type={tag?.type} />
            </View>
          ) : null}

          <View style={styles.textSection}>
            <TitleMdText numberOfLines={2} style={styles.titleStyle}>
              {title?.text}
            </TitleMdText>
            <View style={styles.buttonContainer}>
              <Button
                type="PRIMARY"
                label={
                  buttonTitle?.text ?? rewards.homeOfferCard.activateButtonTitle
                }
                onPress={onActivate}
                size="SMALL"
                width="FULL"
                isDisabled={false}
              />
            </View>
          </View>
        </View>
      </Pressable>
    </View>
  );
};

HomeOfferCard.displayName = 'HomeOfferCard';

const styles = StyleSheet.create({
  backgroundImage: {
    bottom: -42,
    height: 119,
    left: '50%',
    marginLeft: -60,
    position: 'absolute',
    width: 192,
    zIndex: 0,
  },
  buttonContainer: {
    alignSelf: 'center',
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    borderRadius: theme.spacing.xs,
    overflow: 'hidden',
    width: '100%',
  },
  containerActive: {
    borderColor: theme.color.interactive.primary,
    borderWidth: 2,
  },
  containerDisabled: {
    opacity: 0.5,
  },
  containerFocused: {
    borderColor: theme.color.border.focused,
    borderRadius: 4,
    borderWidth: 1,
  },
  containerHover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  containerPressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  contentContainer: {
    paddingHorizontal: theme.spacing.s,
    paddingTop: theme.spacing.s,
  },
  iconSection: {
    backgroundColor: theme.color.expressive.orchid,
    borderRadius: theme.spacing.xs,
    overflow: 'hidden',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  offerItemOverlap: {
    marginLeft: -8,
  },
  offerItemWrapper: {
    backgroundColor: theme.color.background.primary,
    borderRadius: theme.spacing.xs,
    elevation: 2,
    height: 40,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.3,
    shadowRadius: 1.41,
    width: 40,
    zIndex: 1,
  },
  offerItemsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'relative',
  },
  tagContainer: {
    alignSelf: 'center',
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.l,
  },
  textSection: {
    alignSelf: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
    paddingVertical: theme.spacing.m,
  },
  titleStyle: {
    color: theme.color.text.primary,
  },
});

export default HomeOfferCard;
